<script setup lang="ts">
/**
 * CreateUser.vue — Dialog for creating a new user.
 * Supports three roles matching v6:
 *   - Super Admin (accounttype 1)
 *   - Folder Admin (accounttype 2)
 *   - Folder Contributor (accounttype 3)
 */
import { ref, computed, inject, Ref, watch } from 'vue'
import {
	Dialog, DialogContent, DialogDescription, DialogFooter,
	DialogHeader, DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2 } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import { useGroups } from '@/services/Groups'
import { Group } from '@/types/Groups'

const open = defineModel<boolean>({ default: false })

const vhostId = inject<Ref<string> | null>('vhostId', null)

const { getGroups } = useGroups()

// Form state
const firstname = ref('')
const lastname = ref('')
const email = ref('')
const role = ref('2') // default to Folder Contributor
const selectedFolders = ref<number[]>([])
const saving = ref(false)
const groups = ref<Group[]>([])
const errorMsg = ref('')

const roles = [
	{ value: '0', label: 'Super Admin', accounttype: 1 },
	{ value: '1', label: 'Folder Admin', accounttype: 2 },
	{ value: '2', label: 'Folder Contributor', accounttype: 3 },
]

const needsFolders = computed(() => role.value === '1' || role.value === '2')

const isValid = computed(() => {
	if (!firstname.value.trim() || !lastname.value.trim() || !email.value.trim()) return false
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	if (!emailRegex.test(email.value)) return false
	if (needsFolders.value && selectedFolders.value.length === 0) return false
	return true
})

const generatePassword = (): string => {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%'
	let pwd = ''
	for (let i = 0; i < 12; i++) {
		pwd += chars.charAt(Math.floor(Math.random() * chars.length))
	}
	return pwd
}

const toggleFolder = (groupId: number) => {
	const idx = selectedFolders.value.indexOf(groupId)
	if (idx >= 0) {
		selectedFolders.value.splice(idx, 1)
	} else {
		selectedFolders.value.push(groupId)
	}
}

const resetForm = () => {
	firstname.value = ''
	lastname.value = ''
	email.value = ''
	role.value = '2'
	selectedFolders.value = []
	errorMsg.value = ''
}

const createUser = async () => {
	if (!isValid.value || !vhostId?.value) return
	saving.value = true
	errorMsg.value = ''

	const password = generatePassword()
	const selectedRole = roles.find(r => r.value === role.value)!

	try {
		if (role.value === '0' || role.value === '1') {
			// Super Admin or Folder Admin — use vhost.addAdminUser
			const res = await fetch('/services/json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					method: 'vhost.addAdminUser',
					vhostID: vhostId.value,
					userInfo: {
						firstname: firstname.value.trim(),
						lastname: lastname.value.trim(),
						email: email.value.trim(),
						accounttype: selectedRole.accounttype,
						password,
					},
					groups: role.value === '0' ? [] : selectedFolders.value,
				}),
			})
			const json = await res.json()
			if (!json.status) throw new Error(json.message || 'Failed to create admin user')
		} else {
			// Folder Contributor — use users.register
			const res = await fetch('/services/json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					method: 'users.register',
					vhost: vhostId.value,
					userData: {
						firstname: firstname.value.trim(),
						lastname: lastname.value.trim(),
						password,
						accounttype: selectedRole.accounttype,
						email: email.value.trim(),
					},
					options: {
						sendWelcomeEmail: 1,
						emailTemplate: 'contributor-email',
						emailParams: {
							password,
							username: email.value.trim(),
						},
					},
				}),
			})
			const json = await res.json()
			if (!json.status) throw new Error(json.message || 'Failed to create contributor')

			// Assign to selected folders
			if (selectedFolders.value.length > 0 && json.result?.id) {
				for (const folderId of selectedFolders.value) {
					await fetch('/services/json', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							method: 'vhost.addUserToGroup',
							vhostID: vhostId.value,
							userID: json.result.id,
							groupID: folderId,
						}),
					})
				}
			}
		}

		toast.success(`${selectedRole.label} "${firstname.value} ${lastname.value}" created`)
		resetForm()
		open.value = false
	} catch (err: any) {
		errorMsg.value = err.message || 'An error occurred'
	} finally {
		saving.value = false
	}
}

// Fetch groups when dialog opens
watch(open, async (isOpen) => {
	if (isOpen && vhostId?.value) {
		try {
			const result = await getGroups(Number(vhostId.value))
			groups.value = result || []
		} catch (e) {
			console.error('Error fetching groups:', e)
		}
	}
	if (!isOpen) resetForm()
})
</script>

<template>
	<Dialog v-model:open="open">
		<DialogContent class="sm:max-w-[480px]">
			<DialogHeader>
				<DialogTitle>Create User</DialogTitle>
				<DialogDescription>Add a new user to this project.</DialogDescription>
			</DialogHeader>

			<div class="space-y-4 py-2">
				<div class="grid grid-cols-2 gap-3">
					<div class="space-y-1.5">
						<Label class="text-xs">First Name *</Label>
						<Input v-model="firstname" placeholder="First name" class="h-9" />
					</div>
					<div class="space-y-1.5">
						<Label class="text-xs">Last Name *</Label>
						<Input v-model="lastname" placeholder="Last name" class="h-9" />
					</div>
				</div>

				<div class="space-y-1.5">
					<Label class="text-xs">Email Address *</Label>
					<Input v-model="email" type="email" placeholder="user@example.com" class="h-9" />
				</div>

				<div class="space-y-1.5">
					<Label class="text-xs">Role *</Label>
					<Select v-model="role">
						<SelectTrigger class="h-9">
							<SelectValue placeholder="Select role" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="r in roles" :key="r.value" :value="r.value">
								{{ r.label }}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div v-if="needsFolders" class="space-y-1.5">
					<Label class="text-xs">Assign to Folders *</Label>
					<div class="border rounded-md max-h-[160px] overflow-y-auto p-2 space-y-1">
						<template v-if="groups.length > 0">
							<button v-for="group in groups" :key="group.id"
								class="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors text-left"
								@click="toggleFolder(Number(group.id))">
								<Checkbox :checked="selectedFolders.includes(Number(group.id))" />
								<span>{{ group.name }}</span>
							</button>
						</template>
						<p v-else class="text-xs text-muted-foreground text-center py-2">No folders available</p>
					</div>
				</div>

				<p v-if="errorMsg" class="text-xs text-destructive">{{ errorMsg }}</p>
			</div>

			<DialogFooter>
				<Button variant="outline" @click="open = false">Cancel</Button>
				<Button :disabled="!isValid || saving" @click="createUser">
					<Loader2 v-if="saving" class="h-4 w-4 animate-spin mr-2" />
					Create User
				</Button>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>
